import { useQuery } from "@tanstack/react-query";

import { api, type Racecard } from "@/api";
import { Button, HorsePortrait, Skeleton, TierStars } from "@/components/ui";

/** Mounted on demand, sharing the public guide cache with route prefetch. */
export function MarketForm({ card }: { card: Racecard | null }) {
  const guide = useQuery({
    queryKey: ["form-guide", card?.raceId],
    queryFn: () => api.formGuide(card!.raceId),
    enabled: Boolean(card),
    staleTime: 30_000,
  });
  if (!card) {
    return (
      <div className="market-panel__state" role="status">
        Select a published race to view its form.
      </div>
    );
  }
  if (guide.isPending)
    return (
      <div className="market-panel__state" aria-label="Loading form">
        <Skeleton rows={5} />
      </div>
    );
  if (guide.error || guide.data?.raceId !== card.raceId) {
    return (
      <div className="market-panel__state" role="alert">
        <strong>Form temporarily unavailable</strong>
        <p>We could not load matching form for this race.</p>
        <Button variant="secondary" onClick={() => void guide.refetch()}>
          Retry form
        </Button>
      </div>
    );
  }
  const data = guide.data;
  return (
    <div className="market-panel__form" data-form-race-id={data.raceId}>
      <section className="market-panel__analysis" aria-label="Form analysis">
        <h3>Form analysis</h3>
        <p>{data.tips.summary}</p>
        {data.tips.selections.length ? (
          <p className="market-panel__analysis-picks">
            {data.tips.selections
              .flatMap((gate) => {
                const runner = card.runners.find((row) => row.gate === gate);
                return runner ? [`${gate + 1}. ${runner.name}`] : [];
              })
              .join(" · ")}
          </p>
        ) : null}
        <small>Public form analysis, not a guaranteed outcome.</small>
      </section>
      {card.runners.map((runner) => {
        const form = data.runners.find(
          (row) => row.gate === runner.gate && row.horseId === runner.horseId,
        );
        return (
          <details className="market-panel__form-runner" key={runner.horseId}>
            <summary>
              <span
                className="market-panel__gate"
                style={{ borderLeftColor: runner.silk }}
              >
                {runner.gate + 1}
              </span>
              <HorsePortrait compact horse={runner} />
              <span className="market-panel__form-identity">
                <strong>{runner.name}</strong>
                <span>Form {form?.form || "—"}</span>
              </span>
              <span className="market-panel__expand" aria-hidden>
                +
              </span>
            </summary>
            <div className="market-panel__form-detail">
              <TierStars tier={runner.tier} rating={runner.starRating} />
              {form ? (
                <>
                  <dl className="market-panel__stats">
                    <div>
                      <dt>Starts</dt>
                      <dd>{form.races}</dd>
                    </div>
                    <div>
                      <dt>Wins</dt>
                      <dd>{form.wins}</dd>
                    </div>
                    <div>
                      <dt>Top two</dt>
                      <dd>{form.places}</dd>
                    </div>
                    <div>
                      <dt>Top three</dt>
                      <dd>{form.shows}</dd>
                    </div>
                    <div>
                      <dt>Win rate</dt>
                      <dd>{(form.winRateBp / 100).toFixed(1)}%</dd>
                    </div>
                    <div>
                      <dt>Recent finishes</dt>
                      <dd>
                        {form.recent.length
                          ? form.recent
                              .map((finish) =>
                                finish === null ? "—" : finish + 1,
                              )
                              .join(" · ")
                          : "—"}
                      </dd>
                    </div>
                  </dl>
                  {form.comments.map((comment, index) => (
                    <p key={index}>{comment}</p>
                  ))}
                </>
              ) : (
                <p>Form is not available for this runner.</p>
              )}
            </div>
          </details>
        );
      })}
    </div>
  );
}
